<!--
 * @desc Map component containing the map.
 *
 * @author Mike Hamilton <mike@mikehamilton.ca>
-->

<template>
  <v-content>
    <!-- Mapbox component -->
    <mapbox
      access-token
      :map-options="{
        style: {
          glyphs:
            'https://dvn3ptw8fp74t.cloudfront.net/{fontstack}/{range}.pbf',
          version: 8,
          sources: {
            'marker-layer-source': {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: [
                  {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                      type: 'Point',
                      coordinates: [-76.53063297271729, 39.18174077994108]
                    }
                  }
                ]
              }
            }
          },
          // 'marker' layers
          layers: [
            {
              id: 'marker-layer-bottom',
              type: 'circle',
              source: 'marker-layer-source'
            },
            {
              id: 'marker-layer-polygons',
              type: 'circle',
              source: 'marker-layer-source'
            },
            {
              id: 'marker-layer-lines',
              type: 'circle',
              source: 'marker-layer-source'
            },
            {
              id: 'marker-layer-points',
              type: 'circle',
              source: 'marker-layer-source'
            }
          ]
        },
        center: [-122.937613, 50.099706],
        zoom: 12,
        bearing: 150,
        pitchWithRotate: false,
        boxZoom: false,
        // Added so layers can call geojson from urls directly and this add the auth token
        transformRequest: (url, resourceType) => {
          if (
            resourceType === 'Source' &&
            url.startsWith('https://api2.alchemymapping.com')
          ) {
            return {
              url: url,
              headers: { Authorization: 'Bearer ' + this.computedAuthToken }
            };
          }
        }
      }"
      :geolocate-control="{
        show: true,
        position: 'top-right',
        options: {
          positionOptions: {
            enableHighAccuracy: true,
            trackUserLocation: true,
            showUserHeading: true
          }
        }
      }"
      @map-init="mapInitialized"
      @map-load="mapLoad"
      @map-moveend="mapMoveEnd"
      @map-click="mapClicked"
      @geolocate-geolocate="mapGeolocate"
    ></mapbox>
    <div id="latlng">
      {{ mouse.lat.toFixed(7) }}, {{ mouse.lng.toFixed(7) }}
    </div>
  </v-content>
</template>
<style>
.content {
  height: 100%;
}
#map {
  width: 100%;
  height: 100%;
  background-color: lightslategrey !important;
}
.mapboxgl-popup-content {
  background-color: #242424 !important;
  border-radius: 6px;
  font-size: 14px;
  box-shadow: 6px 6px 15px -1px rgba(0, 0, 0, 0.8);
}
.opacity-90 {
  opacity: 0.9;
}
.border-1px-grey {
  border: 1px solid rgb(172, 172, 172);
  padding: 3px;
}
#latlng {
  position: absolute;
  bottom: 0;
  background-color: black;
  opacity: 0.6;
}
</style>
<script>
// Disable no-undef because "mapbox" is defined globally and eslint complains
// about each call to map/this.map
/* eslint-disable no-undef */
import Mapbox from "mapbox-gl-vue";
import "mapbox-gl/dist/mapbox-gl.css";

// CONTOURS
import topolinesContours5mMixin from "../../mixins/scoped/topolinesContours5m";

// SNOWMAKING
import snowmakingFangunsMixin from "../../mixins/scoped/snowmakingFanguns";
import snowmakingHydrantsMixin from "../../mixins/scoped/snowmakingHydrants";
import snowmakingHydrantslabellessMixin from "../../mixins/scoped/snowmakingHydrantslabelless";
import snowmakingAirwatergunsMixin from "../../mixins/scoped/snowmakingAirwaterguns";
import snowmakingPipelinesMixin from "../../mixins/scoped/snowmakingPipelines";
import snowmakingPipeleaksMixin from "../../mixins/scoped/snowmakingPipeleaks";
import snowmakingImagesMixin from "../../mixins/scoped/snowmakingImages";
import snowmakingImagesundergroundMixin from "../../mixins/scoped/snowmakingImagesunderground";
import snowmakingImagesmaintenanceMixin from "../../mixins/scoped/snowmakingImagesmaintenance";
import snowmakingImagesoperationsMixin from "../../mixins/scoped/snowmakingImagesoperations";
import snowmakingImagesotherMixin from "../../mixins/scoped/snowmakingImagesother";
import snowmakingPotableMixin from "../../mixins/scoped/snowmakingPotable";

// LIFT MAINTENANCE
import liftmaintenanceLiftcablesMixin from "../../mixins/scoped/liftmaintenanceCables";
import liftmaintenanceLifttowersMixin from "../../mixins/scoped/liftmaintenanceTowers";

// LIFT OPS
import liftopsdesignatedroutesBlackcombgondolaMixin from "../../mixins/scoped/liftopsdesignatedroutesBlackcombGondola";
import liftopsdesignatedroutesExceleratorMixin from "../../mixins/scoped/liftopsdesignatedroutesExcelerator";
import liftopsdesignatedroutesExcaliburMixin from "../../mixins/scoped/liftopsdesignatedroutesExcalibur";
import liftopsdesignatedroutesShowcaseMixin from "../../mixins/scoped/liftopsdesignatedroutesShowcase";
import liftopsdesignatedroutesGlacierexpressMixin from "../../mixins/scoped/liftopsdesignatedroutesGlacierexpress";
import liftopsdesignatedroutesJerseycreamMixin from "../../mixins/scoped/liftopsdesignatedroutesJerseycream";
import liftopsdesignatedroutesCrystalMixin from "../../mixins/scoped/liftopsdesignatedroutesCrystal";
import liftopsdesignatedroutesSeventhheavenMixin from "../../mixins/scoped/liftopsdesignatedroutesSeventhheaven";
import liftopsdesignatedroutesCatskinnerMixin from "../../mixins/scoped/liftopsdesignatedroutesCatskinner";
import liftopsdesignatedroutesMagicchairMixin from "../../mixins/scoped/liftopsdesignatedroutesMagicchair";
import liftopsdesignatedroutesWhistlercarpetMixin from "../../mixins/scoped/liftopsdesignatedroutesWhistlercarpet";
import liftopsdesignatedroutesSymphonyMixin from "../../mixins/scoped/liftopsdesignatedroutesSymphony";
import liftopsdesignatedroutesFranzMixin from "../../mixins/scoped/liftopsdesignatedroutesFranz";
import liftopsdesignatedroutesHarmonyMixin from "../../mixins/scoped/liftopsdesignatedroutesHarmony";
import liftopsdesignatedroutesPeakchairMixin from "../../mixins/scoped/liftopsdesignatedroutesPeakchair";
import liftopsdesignatedroutesEmeraldMixin from "../../mixins/scoped/liftopsdesignatedroutesEmerald";
import liftopsdesignatedroutesOlympicMixin from "../../mixins/scoped/liftopsdesignatedroutesOlympic";
import liftopsdesignatedroutesWhistlertbarMixin from "../../mixins/scoped/liftopsdesignatedroutesWhistlertbar";
import liftopsdesignatedroutesRedchairMixin from "../../mixins/scoped/liftopsdesignatedroutesRedchair";
import liftopsdesignatedroutesGarbanzoMixin from "../../mixins/scoped/liftopsdesignatedroutesGarbanzo";
import liftopsdesignatedroutesCreeksidegondolaMixin from "../../mixins/scoped/liftopsdesignatedroutesCreeksidegondola";
import liftopsdesignatedroutesBlackcombcarpetMixin from "../../mixins/scoped/liftopsdesignatedroutesBlackcombcarpet";
import liftopsdesignatedroutesFitzsimmonsMixin from "../../mixins/scoped/liftopsdesignatedroutesFitzsimmons";

// LAND USE
import landuseCratenureMixin from "../../mixins/scoped/landuseCratenure";
import landuseWatercoursepolygonsMixin from "../../mixins/scoped/landuseWatercoursepolygons";
import landuseWatercoursesMixin from "../../mixins/scoped/landuseWatercourses";

// UTILITIES
import utilitiesSewergaslinesMixin from "../../mixins/scoped/utilitiesSewergaslines";
import utilitiesSewergaspointsMixin from "../../mixins/scoped/utilitiesSewergaspoints";
import utilitiesCommlinesfiberMixin from "../../mixins/scoped/utilitiesCommlinesfiber";
import utilitiesCommlinescopperMixin from "../../mixins/scoped/utilitiesCommlinescopper";

// BIKE PARK
import bikeparkTrailsMixin from "../../mixins/scoped/bikeparkTrails";
import bikeparkEhspointsMixin from "../../mixins/scoped/bikeparkEhspoints";
import bikeparkBridgesMixin from "../../mixins/scoped/bikeparkBridges";
import bikeparkSignsMixin from "../../mixins/scoped/bikeparkSigns";
import bikeparkSigns2Mixin from "../../mixins/scoped/bikeparkSigns2";
import bikeparkSigns3Mixin from "../../mixins/scoped/bikeparkSigns3";
import bikeparkSigns4Mixin from "../../mixins/scoped/bikeparkSigns4";
import bikeparkSigns5Mixin from "../../mixins/scoped/bikeparkSigns5";

// HIKING
import hikingTrailsMixin from "../../mixins/scoped/hikingTrails";

// ELECTRICAL
import electricalHv25kvMixin from "../../mixins/scoped/electricalHv25kv";
import electricalSubstationsMixin from "../../mixins/scoped/electricalSubstations";
import electricalSecondaryMixin from "../../mixins/scoped/electricalSecondary";
import electricalCircuitsMixin from "../../mixins/scoped/electricalCircuits";
import electricalSplicesMixin from "../../mixins/scoped/electricalSplices";

// PATROL
import patrolPermanentpostsMixin from "../../mixins/scoped/patrolPermanentposts";
import patrolFirstaidMixin from "../../mixins/scoped/patrolFirstaid";
import patrolHelivacMixin from "../../mixins/scoped/patrolHelivac";
import patrolLandmarks from "../../mixins/scoped/patrolLandmarks";

// SIGN SHOP
import signshopSignageMixin from "../../mixins/scoped/signshopSignage";
import signshopInfoboardMixin from "../../mixins/scoped/signshopInfoboard";
import signshopBannersMixin from "../../mixins/scoped/signshopBanners";
import signshopTrailsignsMixin from "../../mixins/scoped/signshopTrailsigns";
import signshopSummersignsMixin from "../../mixins/scoped/signshopSummersigns";
import signshopOtherMixin from "../../mixins/scoped/signshopOther";
import signshopLegalMixin from "../../mixins/scoped/signshopLegal";
import signshopMaintenanceandoperationsMixin from "../../mixins/scoped/signshopMaintenanceandoperations";
import signshopProductsalesandserviceMixin from "../../mixins/scoped/signshopProductsalesandservice";
import signshopFoodandbeverageMixin from "../../mixins/scoped/signshopFoodandbeverage";
import signshopMarketingandsalesMixin from "../../mixins/scoped/signshopMarketingandsales";
import signshopRetailrentalMixin from "../../mixins/scoped/signshopRetailrental";
import signshopSnowschoolMixin from "../../mixins/scoped/signshopSnowschool";
import signshopBaseareaoperationsMixin from "../../mixins/scoped/signshopBaseareaoperations";

// DESIGNATED ROUTES
import designatedroutesStandardMixin from "../../mixins/scoped/designatedroutesStandard";
import designatedroutesSnowmakingMixin from "../../mixins/scoped/designatedroutesSnowmaking";
import designatedroutesParkingMixin from "../../mixins/scoped/designatedroutesParking";
import designatedroutesMaintenanceMixin from "../../mixins/scoped/designatedroutesMaintenance";
import designatedroutesPatrolMixin from "../../mixins/scoped/designatedroutesPatrol";
import designatedRoutesRubbertiresnowmaking from "../../mixins/scoped/designatedroutesRubbertiresnowmaking";
import designatedRoutesRubbertirebikepark from "../../mixins/scoped/designatedroutesRubbertirebikepark";
import designatedRoutesRubbertireliftmaintenance from "../../mixins/scoped/designatedroutesRubbertireliftmaintenance";
import designatedRoutesRubbertireelectricalmaintenance from "../../mixins/scoped/designatedroutesRubbertireelectricalmaintenance";
import designatedRoutesRubbertirepatrol from "../../mixins/scoped/designatedroutesRubbertirepatrol";
import designatedRoutesRubbertirestandard from "../../mixins/scoped/designatedroutesRubbertirestandard";

// GROOMING
import groomingWinchMixin from "../../mixins/scoped/groomingWinch";

// WINTER TRAILS
import wintertrailsSkirunsMixin from "../../mixins/scoped/wintertrailsSkiruns";
import wintertrailsCalloutsMixin from "../../mixins/scoped/wintertrailsCallouts";
import wintertrailsLandmarksMixin from "../../mixins/scoped/wintertrailsLandmarks";
import wintertrailsSkirunpolygonsMixin from "../../mixins/scoped/wintertrailsSkirunpolygons";

// BUILDING MAINTENANCE
import buildingmaintenanceBuildingsMixin from "../../mixins/scoped/buildingmaintenanceBuildings";

// SUMMER OPS
import summeropsFireboxesMixin from "../../mixins/scoped/summeropsFireboxes";
import summeropsCalloutsMixin from "../../mixins/scoped/summeropsCallouts";

// IMAGRY LAYERS
import summer40cm2014Mixin from "../../mixins/scoped/summer40cm2014";
import summer10m2014MuniMixin from "../../mixins/scoped/summer10m2014Muni";
import winter50cm2009BCGovMixin from "../../mixins/scoped/winter50cm2009BCGov";
import summer7_5cm2014Mixin from "../../mixins/scoped/summer7_5cm2018";
import summer10cm2018Mixin from "../../mixins/scoped/summer10cm2018";
import summerDrone2019 from "../../mixins/scoped/summerDrone2019";

// BASEMAP LAYERS
import basemapOpenStreetMapStandardMixin from "../../mixins/scoped/basemapOpenStreetMapStandard.js";

// Popup offsets
let popupOffsets = {
  top: [0, 0],
  "top-left": [0, 0],
  "top-right": [0, 0],
  bottom: [0, 0],
  "bottom-left": [0, 0],
  "bottom-right": [0, 0],
  left: [60, 0],
  right: [0, 0]
};

// Define the popup, it's up here for scoping
let popup = new mapboxgl.Popup({
  closeButton: false,
  closeOnClick: false,
  offset: popupOffsets,
  className: "opacity-90"
});

export default {
  name: "mapComponent",
  data() {
    return {
      mouse: {
        lng: 0.0,
        lat: 0.0
      }
    };
  },
  components: {
    Mapbox
  },
  mixins: [
    topolinesContours5mMixin,

    snowmakingFangunsMixin,
    snowmakingAirwatergunsMixin,
    snowmakingHydrantsMixin,
    snowmakingHydrantslabellessMixin,
    snowmakingPipelinesMixin,
    snowmakingPipeleaksMixin,
    snowmakingImagesMixin,
    snowmakingImagesundergroundMixin,
    snowmakingImagesmaintenanceMixin,
    snowmakingImagesoperationsMixin,
    snowmakingImagesotherMixin,
    snowmakingPotableMixin,

    liftmaintenanceLiftcablesMixin,
    liftmaintenanceLifttowersMixin,

    liftopsdesignatedroutesBlackcombgondolaMixin,
    liftopsdesignatedroutesExceleratorMixin,
    liftopsdesignatedroutesExcaliburMixin,
    liftopsdesignatedroutesShowcaseMixin,
    liftopsdesignatedroutesGlacierexpressMixin,
    liftopsdesignatedroutesJerseycreamMixin,
    liftopsdesignatedroutesCrystalMixin,
    liftopsdesignatedroutesSeventhheavenMixin,
    liftopsdesignatedroutesCatskinnerMixin,
    liftopsdesignatedroutesMagicchairMixin,
    liftopsdesignatedroutesWhistlercarpetMixin,
    liftopsdesignatedroutesSymphonyMixin,
    liftopsdesignatedroutesFranzMixin,
    liftopsdesignatedroutesHarmonyMixin,
    liftopsdesignatedroutesPeakchairMixin,
    liftopsdesignatedroutesEmeraldMixin,
    liftopsdesignatedroutesOlympicMixin,
    liftopsdesignatedroutesWhistlertbarMixin,
    liftopsdesignatedroutesRedchairMixin,
    liftopsdesignatedroutesGarbanzoMixin,
    liftopsdesignatedroutesCreeksidegondolaMixin,
    liftopsdesignatedroutesBlackcombcarpetMixin,
    liftopsdesignatedroutesFitzsimmonsMixin,

    landuseCratenureMixin,
    landuseWatercoursepolygonsMixin,
    landuseWatercoursesMixin,

    utilitiesSewergaslinesMixin,
    utilitiesSewergaspointsMixin,
    utilitiesCommlinesfiberMixin,
    utilitiesCommlinescopperMixin,

    bikeparkTrailsMixin,
    bikeparkEhspointsMixin,
    bikeparkBridgesMixin,
    bikeparkSignsMixin,
    bikeparkSigns2Mixin,
    bikeparkSigns3Mixin,
    bikeparkSigns4Mixin,
    bikeparkSigns5Mixin,

    hikingTrailsMixin,

    electricalHv25kvMixin,
    electricalSubstationsMixin,
    electricalSecondaryMixin,
    electricalCircuitsMixin,
    electricalSplicesMixin,

    patrolPermanentpostsMixin,
    patrolFirstaidMixin,
    patrolHelivacMixin,
    patrolLandmarks,

    signshopSignageMixin,
    signshopInfoboardMixin,
    signshopBannersMixin,
    signshopTrailsignsMixin,
    signshopSummersignsMixin,
    signshopOtherMixin,
    signshopLegalMixin,
    signshopMaintenanceandoperationsMixin,
    signshopProductsalesandserviceMixin,
    signshopFoodandbeverageMixin,
    signshopMarketingandsalesMixin,
    signshopRetailrentalMixin,
    signshopSnowschoolMixin,
    signshopBaseareaoperationsMixin,

    designatedroutesStandardMixin,
    designatedroutesSnowmakingMixin,
    designatedroutesParkingMixin,
    designatedroutesMaintenanceMixin,
    designatedroutesPatrolMixin,
    designatedRoutesRubbertiresnowmaking,
    designatedRoutesRubbertirebikepark,
    designatedRoutesRubbertireliftmaintenance,
    designatedRoutesRubbertireelectricalmaintenance,
    designatedRoutesRubbertirepatrol,
    designatedRoutesRubbertirestandard,

    groomingWinchMixin,

    wintertrailsSkirunsMixin,
    wintertrailsCalloutsMixin,
    wintertrailsLandmarksMixin,
    wintertrailsSkirunpolygonsMixin,

    buildingmaintenanceBuildingsMixin,

    summeropsFireboxesMixin,
    summeropsCalloutsMixin,

    summer40cm2014Mixin,
    summer7_5cm2014Mixin,
    summer10cm2018Mixin,
    summer10m2014MuniMixin,
    summerDrone2019,
    winter50cm2009BCGovMixin,
    basemapOpenStreetMapStandardMixin
  ],
  watch: {
    // If auth state is false, popup the login box
    "$store.state.auth.state": function(newValue) {
      if (newValue === false) {
        this.login();
      }
    },
    // Watch for new zoom 'events'
    "$store.state.zoomTo.timestamp": function() {
      this.map.flyTo({
        center: this.$store.state.zoomTo.center,
        zoom: this.$store.state.zoomTo.level
      });
    },
    // Watch for new drawPoint 'events'
    "$store.state.drawPoint.timestamp": function() {
      // If the drawPoint source/layer already exists, remove them so
      // we can add a new one without littering the map with points
      if (this.map.getSource("drawPoint")) {
        this.map.removeLayer("drawPoint");
        this.map.removeSource("drawPoint");
      }
      // Set a timeout of 250ms so the layers can be removed before
      // trying to add a new one
      setTimeout(() => {
        this.map.addLayer({
          id: "drawPoint",
          type: "circle",
          source: {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [
                {
                  type: "Feature",
                  geometry: {
                    type: "Point",
                    coordinates: this.$store.state.drawPoint.center
                  }
                }
              ]
            }
          },
          paint: {
            "circle-radius": 4,
            "circle-color": "Red",
            "circle-stroke-width": 1,
            "circle-stroke-color": "Black"
          },
          layout: {
            visibility: "visible"
          }
        });
      }, 250);
    }
  },
  computed: {
    computedCenter() {
      return this.$store.state.viewport.center;
    },
    computedBearing() {
      return this.$store.state.viewport.bearing;
    },
    computedPitch() {
      return this.$store.state.viewport.pitch;
    },
    computedZoom() {
      return this.$store.state.viewport.zoom;
    },
    computedLoading() {
      return this.$store.state.general.loading;
    }
  },
  methods: {
    mLevelToString(c) {
      if (c == 1) {
        return "Trainee";
      } else if (c == 2) {
        return "Certified";
      } else if (c == 3) {
        return "Advanced";
      } else {
        return "Unknown";
      }
    },
    mClassToString(c) {
      if (c == 1) {
        return "Uphill & Downhill Travel Permitted";
      } else if (c == 2) {
        return "Downhill Only";
      } else if (c == 3) {
        return "Downhill only during public operating hours";
      } else {
        return "Unknown";
      }
    },
    mapGeolocate(control, position) {
      this.map.flyTo({
        center: [position.coords.longitude, position.coords.latitude],
        zoom: 16
      });
    },
    mapInitialized(map) {
      this.map = map;

      // If we have a lng in the vuex store (coming from LocalStorage at startup..) we assume we have all data
      if (this.computedCenter.lng) {
        // Set the viewport to the previous values
        this.map.setCenter(this.computedCenter);
        this.map.setBearing(this.computedBearing);
        this.map.setPitch(this.computedPitch);
        this.map.setZoom(this.computedZoom);
      }
    },
    mapLoad(map) {
      // Add layers that were previously loaded after last session
      // BASEMAPS
      if (this.computedBasemapOpenStreetMapStandardVisibility)
        this.basemapLayerOpenStreetMapStandardAdd();

      // SATELLITE IMAGRY
      if (this.computedSummer40cm2014Visibility)
        this.mapLayerSatSummer2014Add();
      if (this.computedSummer10m2014MuniVisibility)
        this.mapLayerSatSummer2014MuniAdd();
      if (this.computedWinter50cm2009BCGovVisibility)
        this.mapLayerSatWinter50cm2009BCGovAdd();
      if (this.computedSummer7_5cm2018Visibility)
        this.mapLayerSatSummer2018Add();
      if (this.computedSummer10cm2018Visibility)
        this.mapLayerSat10cmSummer2018Add();
      if (this.computedSummerDrone2019Visibility)
        this.mapLayerSatDroneSummer2019Add();

      // CONTOURS
      if (this.computedTopolinesContours5mMapVisibility)
        this.mapLayerTopolinesContours5mAdd();

      // SNOWMAKING
      if (this.computedSnowmakingAirwatergunsMapVisibility)
        this.mapLayerSnowmakingAirwatergunsAdd();
      if (this.computedSnowmakingFangunsMapVisibility)
        this.mapLayerSnowmakingFangunsAdd();
      if (this.computedSnowmakingHydrantsMapVisibility)
        this.mapLayerSnowmakingHydrantsAdd();
      if (this.computedSnowmakingHydrantslabellessMapVisibility)
        this.mapLayerSnowmakingHydrantslabellessAdd();
      if (this.computedSnowmakingPipelinesMapVisibility)
        this.mapLayerSnowmakingPipelinesAdd();
      if (this.computedSnowmakingPipeleaksMapVisibility)
        this.mapLayerSnowmakingPipeleaksAdd();
      if (this.computedSnowmakingImagesMapVisibility)
        this.mapLayerSnowmakingImagesAdd();
      if (this.computedSnowmakingImagesundergroundMapVisibility)
        this.mapLayerSnowmakingImagesundergroundAdd();
      if (this.computedSnowmakingImagesmaintenanceMapVisibility)
        this.mapLayerSnowmakingImagesmaintenanceAdd();
      if (this.computedSnowmakingImagesoperationsMapVisibility)
        this.mapLayerSnowmakingImagesoperationsAdd();
      if (this.computedSnowmakingImagesotherMapVisibility)
        this.mapLayerSnowmakingImagesotherAdd();
      if (this.computedSnowmakingPotableMapVisibility)
        this.mapLayerSnowmakingPotableAdd();

      // LIFT MAINTENANCE
      if (this.computedLiftmaintenanceLiftcablesMapVisibility)
        this.mapLayerLiftmaintenanceLiftcablesAdd();
      if (this.computedLiftmaintenanceLifttowersMapVisibility)
        this.mapLayerLiftmaintenanceLifttowersAdd();

      // LIFT OPS
      if (this.computedLiftopsdesignatedroutesBlackcombgondolaMapVisibility)
        this.mapLayerLiftopsdesignatedroutesBlackcombgondolaAdd();
      if (this.computedLiftopsdesignatedroutesExceleratorMapVisibility)
        this.mapLayerLiftopsdesignatedroutesExceleratorAdd();
      if (this.computedLiftopsdesignatedroutesExcaliburMapVisibility)
        this.mapLayerLiftopsdesignatedroutesExcaliburAdd();
      if (this.computedLiftopsdesignatedroutesShowcaseMapVisibility)
        this.mapLayerLiftopsdesignatedroutesShowcaseAdd();
      if (this.computedLiftopsdesignatedroutesGlacierexpressMapVisibility)
        this.mapLayerLiftopsdesignatedroutesGlacierexpressAdd();
      if (this.computedLiftopsdesignatedroutesJerseycreamMapVisibility)
        this.mapLayerLiftopsdesignatedroutesJerseycreamAdd();
      if (this.computedLiftopsdesignatedroutesCrystalMapVisibility)
        this.mapLayerLiftopsdesignatedroutesCrystalAdd();
      if (this.computedLiftopsdesignatedroutesSeventhheavenMapVisibility)
        this.mapLayerLiftopsdesignatedroutesSeventhheavenAdd();
      if (this.computedLiftopsdesignatedroutesCatskinnerMapVisibility)
        this.mapLayerLiftopsdesignatedroutesCatskinnerAdd();
      if (this.computedLiftopsdesignatedroutesMagicchairMapVisibility)
        this.mapLayerLiftopsdesignatedroutesMagicchairAdd();
      if (this.computedLiftopsdesignatedroutesWhistlercarpetMapVisibility)
        this.mapLayerLiftopsdesignatedroutesWhistlercarpetAdd();
      if (this.computedLiftopsdesignatedroutesSymphonyMapVisibility)
        this.mapLayerLiftopsdesignatedroutesSymphonyAdd();
      if (this.computedLiftopsdesignatedroutesFranzMapVisibility)
        this.mapLayerLiftopsdesignatedroutesFranzAdd();
      if (this.computedLiftopsdesignatedroutesHarmonyMapVisibility)
        this.mapLayerLiftopsdesignatedroutesHarmonyAdd();
      if (this.computedLiftopsdesignatedroutesPeakchairMapVisibility)
        this.mapLayerLiftopsdesignatedroutesPeakchairAdd();
      if (this.computedLiftopsdesignatedroutesEmeraldMapVisibility)
        this.mapLayerLiftopsdesignatedroutesEmeraldAdd();
      if (this.computedLiftopsdesignatedroutesOlympicMapVisibility)
        this.mapLayerLiftopsdesignatedroutesOlympicAdd();
      if (this.computedLiftopsdesignatedroutesWhistlertbarMapVisibility)
        this.mapLayerLiftopsdesignatedroutesWhistlertbarAdd();
      if (this.computedLiftopsdesignatedroutesRedchairMapVisibility)
        this.mapLayerLiftopsdesignatedroutesRedchairAdd();
      if (this.computedLiftopsdesignatedroutesGarbanzoMapVisibility)
        this.mapLayerLiftopsdesignatedroutesGarbanzoAdd();
      if (this.computedLiftopsdesignatedroutesCreeksidegondolaMapVisibility)
        this.mapLayerLiftopsdesignatedroutesCreeksidegondolaAdd();
      if (this.computedLiftopsdesignatedroutesBlackcombcarpetMapVisibility)
        this.mapLayerLiftopsdesignatedroutesBlackcombcarpetAdd();
      if (this.computedLiftopsdesignatedroutesFitzsimmonsMapVisibility)
        this.mapLayerLiftopsdesignatedroutesFitzsimmonsAdd();

      // LAND USE
      if (this.computedLanduseCratenureMapVisibility)
        this.mapLayerLanduseCratenureAdd();
      if (this.computedLanduseWatercoursepolygonsMapVisibility)
        this.mapLayerLanduseWatercoursepolygonsAdd();
      if (this.computedLanduseWatercoursesMapVisibility)
        this.mapLayerLanduseWatercoursesAdd();

      // UTILITIES
      if (this.computedUtilitiesSewergaslinesMapVisibility)
        this.mapLayerUtilitiesSewergaslinesAdd();
      if (this.computedUtilitiesSewergaspointsMapVisibility)
        this.mapLayerUtilitiesSewergaspointsAdd();
      if (this.computedUtilitiesCommlinesfiberMapVisibility)
        this.mapLayerUtilitiesCommlinesfiberAdd();
      if (this.computedUtilitiesCommlinescopperMapVisibility)
        this.mapLayerUtilitiesCommlinescopperAdd();

      // BIKE PARK
      if (this.computedBikeparkTrailsMapVisibility)
        this.mapLayerBikeparkTrailsAdd();
      if (this.computedBikeparkEhspointsMapVisibility)
        this.mapLayerBikeparkEhspointspointsAdd();
      if (this.computedBikeparkBridgesMapVisibility)
        this.mapLayerBikeparkBridgesAdd();
      if (this.computedBikeparkSignsMapVisibility)
        this.mapLayerBikeparkSignsAdd();
      if (this.computedBikeparkSigns2MapVisibility)
        this.mapLayerBikeparkSigns2Add();
      if (this.computedBikeparkSigns3MapVisibility)
        this.mapLayerBikeparkSigns3Add();
      if (this.computedBikeparkSigns4MapVisibility)
        this.mapLayerBikeparkSigns4Add();
      if (this.computedBikeparkSigns5MapVisibility)
        this.mapLayerBikeparkSigns5Add();

      // HIKING
      if (this.computedHikingTrailsMapVisibility)
        this.mapLayerHikingTrailsAdd();

      // ELECTRICAL
      if (this.computedElectricalHv25kvMapVisibility)
        this.mapLayerElectricalHv25kvAdd();
      if (this.computedElectricalSubstationsMapVisibility)
        this.mapLayerElectricalSubstationsAdd();
      if (this.computedElectricalSecondaryMapVisibility)
        this.mapLayerElectricalSecondaryAdd();
      if (this.computedElectricalCircuitsMapVisibility)
        this.mapLayerElectricalCircuitsAdd();
      if (this.computedElectricalSplicesMapVisibility)
        this.mapLayerElectricalSplicesAdd();

      // PATROL
      if (this.computedPatrolPermanentpostsMapVisibility)
        this.mapLayerPatrolPermanentpostsAdd();
      if (this.computedPatrolFirstaidMapVisibility)
        this.mapLayerPatrolFirstaidAdd();
      if (this.computedPatrolHelivacMapVisibility)
        this.mapLayerPatrolHelivacAdd();
      if (this.computedPatrolLandmarksMapVisibility)
        this.mapLayerPatrolLandmarksAdd();

      // SIGN SHOP
      if (this.computedSignshopSignageMapVisibility)
        this.mapLayerSignshopSignageAdd();

      // SIGN SHOP INFO BOARD
      if (this.computedSignshopInfoboardMapVisibility)
        this.mapLayerSignshopInfoboardAdd();

      // SIGN SHOP TRAIL SIGNS
      if (this.computedSignshopTrailsignsMapVisibility)
        this.mapLayerSignshopTrailsignsAdd();

      // SIGN SHOP BANNERS
      if (this.computedSignshopBannersMapVisibility)
        this.mapLayerSignshopBannersAdd();

      // SIGN SHOP LEGAL
      if (this.computedSignshopLegalMapVisibility)
        this.mapLayerSignshopLegalAdd();

      // SIGN SHOP SUMMER SIGNS
      if (this.computedSignshopSummersignsMapVisibility)
        this.mapLayerSignshopSummersignsAdd();

      // SIGN SHOP OTHER
      if (this.computedSignshopOtherMapVisibility)
        this.mapLayerSignshopOtherAdd();

      // SIGN SHOP MAINT & OPS
      if (this.computedSignshopMaintenanceandoperationsMapVisibility)
        this.mapLayerSignshopMaintenanceandoperationsAdd();

      // SIGN SHOP PRODUCT SALES AND SERVICE
      if (this.computedSignshopProductsalesandserviceMapVisibility)
        this.mapLayerSignshopProductsalesandserviceAdd();

      // SIGN SHOP FOOD AND BEV
      if (this.computedSignshopFoodandbeverageMapVisibility)
        this.mapLayerSignshopFoodandbeverageAdd();

      // SIGN SHOP MARKETING AND SALES
      if (this.computedSignshopMarketingandsalesMapVisibility)
        this.mapLayerSignshopMarketingandsalesAdd();

      // SIGN SHOP RETAIL RENTAL
      if (this.computedSignshopRetailrentalMapVisibility)
        this.mapLayerSignshopRetailrentalAdd();

      // SIGN SHOP SNOWSCHOOL
      if (this.computedSignshopSnowschoolMapVisibility)
        this.mapLayerSignshopSnowschoolAdd();

      // SIGN SHOP BASE AREA OPS
      if (this.computedSignshopBaseareaopertionsMapVisibility)
        this.mapLayerSignshopBaseareaopertionsAdd();

      // GROOMING
      if (this.computedGroomingWinchMapVisibility)
        this.mapLayerGroomingWinchAdd();

      // WINTER TRAILS
      if (this.computedWintertrailsSkirunsMapVisibility)
        this.mapLayerWintertrailsSkirunsAdd();
      if (this.computedWintertrailsCalloutsMapVisibility)
        this.mapLayerWintertrailsCalloutsAdd();
      if (this.computedWintertrailsLandmarksMapVisibility)
        this.mapLayerWintertrailsLandmarksAdd();
      if (this.computedWintertrailsSkirunpolygonsMapVisibility)
        this.mapLayerWintertrailsSkirunpolygonsAdd();

      // DESIGNATED ROUTES
      if (this.computedDesignatedroutesStandardMapVisibility)
        this.mapLayerDesignatedroutesStandardAdd();
      if (this.computedDesignatedroutesSnowmakingMapVisibility)
        this.mapLayerDesignatedroutesSnowmakingAdd();
      if (this.computedDesignatedroutesParkingMapVisibility)
        this.mapLayerDesignatedroutesParkingAdd();
      if (this.computedDesignatedroutesMaintenanceMapVisibility)
        this.mapLayerDesignatedroutesMaintenanceAdd();
      if (this.computedDesignatedroutesPatrolMapVisibility)
        this.mapLayerDesignatedroutesPatrolAdd();
      if (this.computedDesignatedroutesRubbertiresnowmakingMapVisibility)
        this.mapLayerDesignatedroutesRubbertiresnowmakingAdd();
      if (this.computedDesignatedroutesRubbertirebikeparkMapVisibility)
        this.mapLayerDesignatedroutesRubbertirebikeparkAdd();
      if (this.computedDesignatedroutesRubbertireliftmaintenanceMapVisibility)
        this.mapLayerDesignatedroutesRubbertireliftmaintenanceAdd();
      if (
        this
          .computedDesignatedroutesRubbertireelectricalmaintenanceMapVisibility
      )
        this.mapLayerDesignatedroutesRubbertireelectricalmaintenanceAdd();
      if (this.computedDesignatedroutesRubbertirepatrolMapVisibility)
        this.mapLayerDesignatedroutesRubbertirepatrolAdd();
      if (this.computedDesignatedroutesRubbertirestandardMapVisibility)
        this.mapLayerDesignatedroutesRubbertirestandardAdd();

      // BUILDING MAINTENANCE
      if (this.computedBuildingmaintenanceBuildingsMapVisibility)
        this.mapLayerBuildingmaintenanceBuildingsAdd();

      // SUMMEROPS
      if (this.computedSummeropsFireboxesMapVisibility)
        this.mapLayerSummeropsFireboxesAdd();
      if (this.computedSummeropsCalloutsMapVisibility)
        this.mapLayerSummeropsCalloutsAdd();

      let lastHover = null;

      map.on("mousemove", e => {
        map.resize(); // hack to resize after Auth0 login, todo: look into performance?
        var features = map.queryRenderedFeatures(e.point);

        // Lat/lng of mouse to display on screen
        this.mouse.lng = e.lngLat.lng;
        this.mouse.lat = e.lngLat.lat;

        // Close all popups when not hovering over something
        if (!features.length && popup.isOpen()) popup.remove();

        // SNOWMAKING HYDRANTS HOVER POPUP
        if (features.length && features[0].layer.source == "hydrants") {
          popup
            .setLngLat(features[0].geometry.coordinates)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Water</td>
                    <td>${features[0].properties.water}</td>
                  </tr>
                  <tr>
                    <td>Air</td>
                    <td>${features[0].properties.air}</td>
                  </tr>
                  <tr>
                    <td>60A</td>
                    <td>${features[0].properties["60a"]}</td>
                  </tr>
                  <tr>
                    <td>30A</td>
                    <td>${features[0].properties["30a"]}</td>
                  </tr>
                  <tr>
                    <td>Circuit</td>
                    <td>${features[0].properties.circuit}</td>
                  </tr>
                  <tr>
                    <td style="padding-right: 10px;">Power Source</td>
                    <td>${features[0].properties.elec_maint}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // SNOWMAKING HYDRANTS HOVER POPUP
        if (
          features.length &&
          features[0].layer.source == "hydrantslabelless"
        ) {
          popup
            .setLngLat(features[0].geometry.coordinates)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Water</td>
                    <td>${features[0].properties.water}</td>
                  </tr>
                  <tr>
                    <td>Air</td>
                    <td>${features[0].properties.air}</td>
                  </tr>
                  <tr>
                    <td>60A</td>
                    <td>${features[0].properties["60a"]}</td>
                  </tr>
                  <tr>
                    <td>30A</td>
                    <td>${features[0].properties["30a"]}</td>
                  </tr>
                  <tr>
                    <td>Circuit</td>
                    <td>${features[0].properties.circuit}</td>
                  </tr>
                  <tr>
                    <td style="padding-right: 10px;">Power Source</td>
                    <td>${features[0].properties.elec_maint}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // SNOWMAKING FANGUNS HOVER POPUP
        if (features.length && features[0].layer.source == "fanguns") {
          popup
            .setLngLat(features[0].geometry.coordinates)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>${features[0].properties.amps}A ${features[0].properties.gun_style} #${features[0].properties.gun_num}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // SNOWMAKING AIRWATERGUNS HOVER POPUP
        if (features.length && features[0].layer.source == "airwaterguns") {
          popup
            .setLngLat(features[0].geometry.coordinates)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>${features[0].properties.length}' ${features[0].properties.model} #${features[0].properties.gun_number}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }
        // SNOWMAKING PIPELEAKS HOVER POPUP
        if (
          features.length &&
          features[0].layer.source == "snowmaking-pipeleaks-source"
        ) {
          popup
            .setLngLat(features[0].geometry.coordinates)
            .setHTML(
              `
                <table>
                  <tr> 
                    <td>Repair date:</td>
                    <td>${
                      features[0].properties.rep_date !== "null"
                        ? features[0].properties.rep_date.split("T")[0]
                        : ""
                    }</td>
                  </tr>
                    <tr> 
                    <td>Pipe Name:</td>
                    <td>${
                      features[0].properties.run_pipeli !== "null"
                        ? features[0].properties.run_pipeli
                        : ""
                    }</td>
                  </tr>
                    <tr> 
                    <td>Repair complete:</td>
                    <td>${
                      features[0].properties.rep_comple !== "null"
                        ? features[0].properties.rep_comple
                        : ""
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // SNOWMAKING POTABLE HOVER POPUP
        if (
          features.length &&
          features[0].layer.source == "snowmaking-potable-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr> 
                    <td>Pipe Name:</td>
                    <td>${
                      features[0].properties.pipe_name !== "null"
                        ? features[0].properties.pipe_name
                        : ""
                    }</td>
                  </tr>
                    <tr> 
                    <td>Pipe Type:</td>
                    <td>${
                      features[0].properties.pipe_type !== "null"
                        ? features[0].properties.pipe_type
                        : ""
                    }</td>
                  </tr>
                    <tr> 
                    <td>Pipe diameter (inch):</td>
                    <td>${
                      features[0].properties.diam_in !== "null"
                        ? features[0].properties.diam_in
                        : ""
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          (features[0].layer.source == "snowmaking-images-source" ||
            features[0].layer.source == "snowmaking-imagesunderground-source" ||
            features[0].layer.source == "snowmaking-imagesmaintenance-source" ||
            features[0].layer.source == "snowmaking-imagesoperations-source" ||
            features[0].layer.source == "snowmaking-imagesother-source")
        ) {
          popup
            .setLngLat(features[0].geometry.coordinates)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>${features[0].properties.title}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // ELECTRICAL CIRCUITS HOVER POPUP
        if (
          features.length &&
          features[0].layer.source == "circuits" &&
          features[0].layer.id != "clusters" &&
          features[0].layer.id != "cluster-count"
        ) {
          popup
            .setLngLat(features[0].geometry.coordinates)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Hydrant #</td>
                    <td>${features[0].properties.hydrnt_num}</td>
                  </tr>
                  <tr>
                    <td style="padding-right: 10px;">Power Source</td>
                    <td>${features[0].properties.elec_maint}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // 25kv Popup
        if (
          features.length &&
          features[0].layer.id == "electrical-hv25kv-fatfinger"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Extent</td>
                    <td>${features[0].properties.cbl_extent}</td>
                  </tr>
                  <tr>
                    <td>Wire Size</td>
                    <td>${features[0].properties.wire_size}</td>
                  </tr>
                  <tr>
                    <td>Number of Cables</td>
                    <td>${features[0].properties.no_cables}</td>
                  </tr>
                  <tr>
                    <td>Cable length</td>
                    <td>${parseInt(features[0].properties.cbl_length).toFixed(
                      0
                    )}m</td>
                  </tr>
                  <tr>
                    <td>Energized</td>
                    <td>${features[0].properties.energized}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // ELECTRICAL CIRCUITS HOVER POPUP
        if (
          features.length &&
          features[0].layer.source == "electrical-splices-source"
        ) {
          popup
            .setLngLat(features[0].geometry.coordinates)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: </td>
                    <td>${
                      features[0].properties.text !== "null"
                        ? features[0].properties.text
                        : ""
                    }</td>
                  </tr>
                  <tr>
                    <td>Cable Type: </td>
                    <td>${
                      features[0].properties.cable_type !== "null"
                        ? features[0].properties.cable_type
                        : ""
                    }</td>
                  </tr>
                  <tr>
                    <td>Cable Size: </td>
                    <td>${
                      features[0].properties.cbl_size !== "null"
                        ? features[0].properties.cbl_size
                        : ""
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Designated routes Popup
        if (
          features.length &&
          features[0].layer.id == "designatedroutes-standard-fatfinger"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td class="border-1px-grey">Name</td>
                    <td class="border-1px-grey">${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Department</td>
                    <td class="border-1px-grey">${features[0].properties.department}</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Level</td>
                    <td class="border-1px-grey">${features[0].properties.op_level}</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Route Specification</td>
                    <td class="border-1px-grey">${features[0].properties.route_spec}</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Caution Level</td>
                    <td class="border-1px-grey">${features[0].properties.caution_lvl}</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Comments</td>
                    <td class="border-1px-grey">${features[0].properties.comments}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Patrol designated routes Popup
        if (
          features.length &&
          features[0].layer.id == "designatedroutes-patrol-fatfinger"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td class="border-1px-grey">Name</td>
                    <td class="border-1px-grey">${
                      features[0].properties.name
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Level</td>
                    <td class="border-1px-grey">${
                      features[0].properties.op_level
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Route Specification</td>
                    <td class="border-1px-grey">${this.mClassToString(
                      features[0].properties.class
                    )}</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Caution Level</td>
                    <td class="border-1px-grey">${
                      features[0].properties.caution_lv
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Comments</td>
                    <td class="border-1px-grey">${
                      features[0].properties.comments
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Designated routes Popup rubbertire snowmaking
        if (
          features.length &&
          features[0].layer.id ==
            "designatedroutes-rubbertiresnowmaking-fatfinger"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td class="border-1px-grey">Road Name</td>
                    <td class="border-1px-grey">${
                      features[0].properties.rd_name
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Level</td>
                    <td class="border-1px-grey">${
                      features[0].properties.level
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Vehicle Type</td>
                    <td class="border-1px-grey">${
                      features[0].properties.v_type
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Road Class</td>
                    <td class="border-1px-grey">${
                      features[0].properties.rd_class
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Comments</td>
                    <td class="border-1px-grey">${
                      features[0].properties.comments === "null"
                        ? ""
                        : features[0].properties.comments
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Designated routes Popup rubbertire bikepark
        if (
          features.length &&
          features[0].layer.id ==
            "designatedroutes-rubbertirebikepark-fatfinger"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td class="border-1px-grey">Road Name</td>
                    <td class="border-1px-grey">${
                      features[0].properties.rd_name
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Level</td>
                    <td class="border-1px-grey">${
                      features[0].properties.level
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Vehicle Type</td>
                    <td class="border-1px-grey">${
                      features[0].properties.v_type
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Road Class</td>
                    <td class="border-1px-grey">${
                      features[0].properties.rd_class
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Comments</td>
                    <td class="border-1px-grey">${
                      features[0].properties.comments === "null"
                        ? ""
                        : features[0].properties.comments
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Designated routes Popup rubbertire liftmaintenance
        if (
          features.length &&
          features[0].layer.id ==
            "designatedroutes-rubbertireliftmaintenance-fatfinger"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td class="border-1px-grey">Road Name</td>
                    <td class="border-1px-grey">${
                      features[0].properties.rd_name
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Level</td>
                    <td class="border-1px-grey">${
                      features[0].properties.level
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Vehicle Type</td>
                    <td class="border-1px-grey">${
                      features[0].properties.v_type
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Road Class</td>
                    <td class="border-1px-grey">${
                      features[0].properties.rd_class
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Comments</td>
                    <td class="border-1px-grey">${
                      features[0].properties.comments === "null"
                        ? ""
                        : features[0].properties.comments
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Designated routes Popup rubbertire electricalmaintenance
        if (
          features.length &&
          features[0].layer.id ==
            "designatedroutes-rubbertireelectricalmaintenance-fatfinger"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td class="border-1px-grey">Road Name</td>
                    <td class="border-1px-grey">${
                      features[0].properties.rd_name
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Level</td>
                    <td class="border-1px-grey">${
                      features[0].properties.level
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Vehicle Type</td>
                    <td class="border-1px-grey">${
                      features[0].properties.v_type
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Road Class</td>
                    <td class="border-1px-grey">${
                      features[0].properties.rd_class
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Comments</td>
                    <td class="border-1px-grey">${
                      features[0].properties.comments === "null"
                        ? ""
                        : features[0].properties.comments
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Designated routes Popup rubbertire patrol
        if (
          features.length &&
          features[0].layer.id == "designatedroutes-rubbertirepatrol-fatfinger"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td class="border-1px-grey">Road Name</td>
                    <td class="border-1px-grey">${
                      features[0].properties.rd_name
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Level</td>
                    <td class="border-1px-grey">${
                      features[0].properties.level
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Vehicle Type</td>
                    <td class="border-1px-grey">${
                      features[0].properties.v_type
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Road Class</td>
                    <td class="border-1px-grey">${
                      features[0].properties.rd_class
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Comments</td>
                    <td class="border-1px-grey">${
                      features[0].properties.comments === "null"
                        ? ""
                        : features[0].properties.comments
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Designated routes Popup rubbertire safety
        if (
          features.length &&
          features[0].layer.id ==
            "designatedroutes-rubbertirestandard-fatfinger"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td class="border-1px-grey">Road Name</td>
                    <td class="border-1px-grey">${
                      features[0].properties.rd_name
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Level</td>
                    <td class="border-1px-grey">${
                      features[0].properties.level
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Vehicle Type</td>
                    <td class="border-1px-grey">${
                      features[0].properties.v_type
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Road Class</td>
                    <td class="border-1px-grey">${
                      features[0].properties.rd_class
                    }</td>
                  </tr>
                  <tr>
                    <td class="border-1px-grey">Comments</td>
                    <td class="border-1px-grey">${
                      features[0].properties.comments === "null"
                        ? ""
                        : features[0].properties.comments
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Buildings Popup
        if (
          features.length &&
          features[0].layer.id == "buildingmaintenance-buildings-fill"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>${features[0].properties.asset_name}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Winch Popup
        if (
          features.length &&
          (features[0].layer.id == "grooming-winch" ||
            features[0].layer.id == "grooming-winch-labels")
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Anchor Number: </td>
                    <td>${features[0].properties.a_number}</td>
                  </tr>
                  <tr>
                    <td>Overall Condition: </td>
                    <td>${
                      features[0].properties.overall_anch_cond !== "null"
                        ? features[0].properties.overall_anch_cond
                        : "Unknown"
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Patrol Landmarks Popup
        if (
          features.length &&
          features[0].source == "patrol-landmarks-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>${features[0].properties.place_name}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Wintertrails Landmarks Popup
        if (
          features.length &&
          features[0].source == "wintertrails-landmarks-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>${features[0].properties.place_name}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Wintertrails Polygons Popup
        if (
          features.length &&
          features[0].source == "wintertrails-skirunpolygons-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Run: ${features[0].properties.run}</td>
                  </tr>
                  <tr>
                    <td>Run Segment: ${features[0].properties.run_seg}</td>
                  </tr>
                  <tr>
                    <td>Surface Area(ac): ${features[0].properties.sarea_acre.toFixed(
                      2
                    )}</td>
                  </tr>
                  <tr>
                    <td>Average Slope: ${features[0].properties.avg_slope.toFixed(
                      2
                    )}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Patrol First-aid Popup
        if (features.length && features[0].source == "patrol-firstaid-source") {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.NAME}</td>
                  </tr>
                  <tr>
                    <td>Type: ${features[0].properties.CACHE_TYPE}</td>
                  </tr>
                  <tr>
                    <td>Description: ${features[0].properties.LOC_DESCRIPT}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Patrol Helivac Popup
        if (features.length && features[0].source == "patrol-helivac-source") {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Pad Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td>Approx Elevation (m): ${features[0].properties.app_elev_m}</td>
                  </tr>

                </table>
              `
            )
            .addTo(map);
        }

        // CRA tenure & park boundaries popup (Removed as per Heather)
        // if (
        //   features.length &&
        //   features[0].source == "landuse-cratenure-source"
        // ) {
        //   popup
        //     .setLngLat(e.lngLat)
        //     .setHTML(
        //       `
        //         <table>
        //           <tr>
        //             <td>${features[0].properties.name}</td>
        //           </tr>
        //         </table>
        //       `
        //     )
        //     .addTo(map);
        // }

        // Watercourse polygons popup
        if (
          features.length &&
          features[0].source == "landuse-watercoursepolygons-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${
                      features[0].properties.assetname === "null"
                        ? "Unknown"
                        : features[0].properties.assetname
                    }</td>
                  </tr>
                                    <tr>
                    <td>Type: ${features[0].properties.type}</td>
                  </tr>
                                    <tr>
                    <td>Perimeter(m): ${features[0].properties.shape_leng.toFixed(
                      2
                    )}</td>
                  </tr>
                  <tr>
                    <td>Area(m2): ${features[0].properties.shape_area.toFixed(
                      2
                    )}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Watercourses popup
        if (
          features.length &&
          features[0].source == "landuse-watercourses-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${
                      features[0].properties.assetname === "null"
                        ? "Unknown"
                        : features[0].properties.assetname
                    }</td>
                  </tr>
                  <tr>
                    <td>Type: ${features[0].properties.type}</td>
                  </tr>
                  <tr>
                    <td>Length(m): ${features[0].properties.shape_leng.toFixed(
                      2
                    )}</td>
                  </tr>
                  <tr>
                    <td>Classification: ${
                      features[0].properties.classification === "null"
                        ? "Unknown"
                        : features[0].properties.classification
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Bikepark Bridges popup
        if (
          features.length &&
          features[0].source == "bikepark-bridges-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Trail Name: ${features[0].properties.trail_name}</td>
                  </tr>
                  <tr>
                    <td>Bridge Number: ${features[0].properties.bridge_num}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift Ops Designated Routes Blackcomb Gondola popup
        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-blackcombgondola-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-blackcombgondolaStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes Excelerator popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-excelerator-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-exceleratorStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes Excalibur popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-excalibur-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-excaliburStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes Showcase popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-showcase-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-showcaseStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes Glacierexpress popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-glacierexpress-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-glacierexpressStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes Jerseycream popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-jerseycream-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-jerseycreamStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes Crystal popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-crystal-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-crystalStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes Seventhheaven popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-seventhheaven-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-seventhheavenStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes Catskinner popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-catskinner-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-catskinnerStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes Magicchair popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-magicchair-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-magicchairStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes Whistlercarpet popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-whistlercarpet-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-whistlercarpetStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes symphony popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-symphony-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-symphonyStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes franz popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-franz-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-franzStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // DESIGNATED ROUTES STANDARD hover effect
        if (
          features.length &&
          features[0].layer.source == "designatedroutes-standard-source"
        ) {
          lastHover = "designatedroutes-standard-source";
          map.setFilter("designatedroutes-standard-hover", [
            "all",
            ["in", "name"].concat(features[0].properties.name)
          ]);
        } else {
          if (
            !features.length &&
            lastHover === "designatedroutes-standard-source"
          ) {
            map.setFilter("designatedroutes-standard-hover", [
              "all",
              ["in", "name", "abc123"]
            ]);
            lastHover = null;
          }
        }

        // Lift ops designated routes harmony popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-harmony-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-harmonyStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes peakchair popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-peakchair-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-peakchairStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes emerald popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-emerald-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-emeraldStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes olympic popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-olympic-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-olympicStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes whistlertbar popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-whistlertbar-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-whistlertbarStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes redchair popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-redchair-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-redchairStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes garbanzo popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-garbanzo-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-garbanzoStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes creeksidegondola popups
        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-creeksidegondola-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-creeksidegondolaStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes blackcombcarpet popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-blackcombcarpet-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-blackcombcarpetStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // Lift ops designated routes fitzsimmons popups
        if (
          features.length &&
          features[0].source == "liftopsdesignatedroutes-fitzsimmons-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Description: ${features[0].properties.label}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Route Instructions: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        if (
          features.length &&
          features[0].source ==
            "liftopsdesignatedroutes-fitzsimmonsStoppoints-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Name: ${features[0].properties.name}</td>
                  </tr>
                  <tr>
                    <td> ${
                      features[0].properties.notes == "null"
                        ? ""
                        : "Notes: " + features[0].properties.notes
                    }</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // SUMMER OPS FIREBOXES
        if (
          features.length &&
          features[0].source == "summerops-fireboxes-source"
        ) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(
              `
                <table>
                  <tr>
                    <td>Box Number: ${features[0].properties.box_num}</td>
                  </tr>
                  <tr>
                    <td>Location: ${features[0].properties.location}</td>
                  </tr>
                  <tr>
                    <td>Pump Type: ${features[0].properties.pump_type}</td>
                  </tr>
                </table>
              `
            )
            .addTo(map);
        }

        // DESIGNATED ROUTES STANDARD hover effect
        if (
          features.length &&
          features[0].layer.source == "designatedroutes-standard-source"
        ) {
          lastHover = "designatedroutes-standard-source";
          map.setFilter("designatedroutes-standard-hover", [
            "all",
            ["in", "name"].concat(features[0].properties.name)
          ]);
        } else {
          if (
            !features.length &&
            lastHover === "designatedroutes-standard-source"
          ) {
            map.setFilter("designatedroutes-standard-hover", [
              "all",
              ["in", "name", "abc123"]
            ]);
            lastHover = null;
          }
        }

        // DESIGNATED ROUTES RUBBER TIRE Snowmaking hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "designatedroutes-rubbertiresnowmaking-source"
        ) {
          lastHover = "designatedroutes-rubbertiresnowmaking-source";
          map.setFilter("designatedroutes-rubbertiresnowmaking-hover", [
            "all",
            ["in", "id"].concat(features[0].properties.id)
          ]);
        } else {
          if (
            !features.length &&
            lastHover === "designatedroutes-rubbertiresnowmaking-source"
          ) {
            map.setFilter("designatedroutes-rubbertiresnowmaking-hover", [
              "all",
              ["in", "id", "abc123"]
            ]);
            lastHover = null;
          }
        }

        // DESIGNATED ROUTES RUBBER TIRE bikepark hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "designatedroutes-rubbertirebikepark-source"
        ) {
          lastHover = "designatedroutes-rubbertirebikepark-source";
          map.setFilter("designatedroutes-rubbertirebikepark-hover", [
            "all",
            ["in", "id"].concat(features[0].properties.id)
          ]);
        } else {
          if (
            !features.length &&
            lastHover === "designatedroutes-rubbertirebikepark-source"
          ) {
            map.setFilter("designatedroutes-rubbertirebikepark-hover", [
              "all",
              ["in", "id", "abc123"]
            ]);
            lastHover = null;
          }
        }

        // DESIGNATED ROUTES RUBBER TIRE liftmaintenance hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "designatedroutes-rubbertireliftmaintenance-source"
        ) {
          lastHover = "designatedroutes-rubbertireliftmaintenance-source";
          map.setFilter("designatedroutes-rubbertireliftmaintenance-hover", [
            "all",
            ["in", "id"].concat(features[0].properties.id)
          ]);
        } else {
          if (
            !features.length &&
            lastHover === "designatedroutes-rubbertireliftmaintenance-source"
          ) {
            map.setFilter("designatedroutes-rubbertireliftmaintenance-hover", [
              "all",
              ["in", "id", "abc123"]
            ]);
            lastHover = null;
          }
        }

        // DESIGNATED ROUTES RUBBER TIRE electricalmaintenance hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "designatedroutes-rubbertireelectricalmaintenance-source"
        ) {
          lastHover = "designatedroutes-rubbertireelectricalmaintenance-source";
          map.setFilter(
            "designatedroutes-rubbertireelectricalmaintenance-hover",
            ["all", ["in", "id"].concat(features[0].properties.id)]
          );
        } else {
          if (
            !features.length &&
            lastHover ===
              "designatedroutes-rubbertireelectricalmaintenance-source"
          ) {
            map.setFilter(
              "designatedroutes-rubbertireelectricalmaintenance-hover",
              ["all", ["in", "id", "abc123"]]
            );
            lastHover = null;
          }
        }

        // DESIGNATED ROUTES RUBBER TIRrepatrol hover effect
        if (
          features.length &&
          features[0].layer.source == "designatedroutes-rubbertirepatrol-source"
        ) {
          lastHover = "designatedroutes-rubbertirepatrol-source";
          map.setFilter("designatedroutes-rubbertirepatrol-hover", [
            "all",
            ["in", "id"].concat(features[0].properties.id)
          ]);
        } else {
          if (
            !features.length &&
            lastHover === "designatedroutes-rubbertirepatrol-source"
          ) {
            map.setFilter("designatedroutes-rubbertirepatrol-hover", [
              "all",
              ["in", "id", "abc123"]
            ]);
            lastHover = null;
          }
        }

        // DESIGNATED ROUTES RUBBER TIRE standard hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "designatedroutes-rubbertirestandard-source"
        ) {
          lastHover = "designatedroutes-rubbertirestandard-source";
          map.setFilter("designatedroutes-rubbertirestandard-hover", [
            "all",
            ["in", "id"].concat(features[0].properties.id)
          ]);
        } else if (
          !features.length &&
          lastHover === "designatedroutes-rubbertirestandard-source"
        ) {
          map.setFilter("designatedroutes-rubbertirestandard-hover", [
            "all",
            ["in", "name", "abc123"]
          ]);
          lastHover = null;
        }

        // DESIGNATED ROUTES SNOWMAKING hover effect
        if (
          features.length &&
          features[0].layer.source == "designatedroutes-snowmaking-source"
        ) {
          lastHover = "designatedroutes-snowmaking-source";
          map.setFilter("designatedroutes-snowmaking-hover", [
            "all",
            ["in", "name"].concat(features[0].properties.name)
          ]);
        } else if (
          !features.length &&
          lastHover === "designatedroutes-snowmaking-source"
        ) {
          map.setFilter("designatedroutes-snowmaking-hover", [
            "all",
            ["in", "name", "abc123"]
          ]);
          lastHover = null;
        }

        // DESIGNATED ROUTES MAINTENANCE hover effect
        if (
          features.length &&
          features[0].layer.source == "designatedroutes-maintenance-source"
        ) {
          lastHover = "designatedroutes-maintenance-source";
          map.setFilter("designatedroutes-maintenance-hover", [
            "all",
            ["in", "name"].concat(features[0].properties.name)
          ]);
        } else if (
          !features.length &&
          lastHover === "designatedroutes-maintenance-source"
        ) {
          map.setFilter("designatedroutes-maintenance-hover", [
            "all",
            ["in", "name", "abc123"]
          ]);
          lastHover = null;
        }

        // DESIGNATED ROUTES MAINTENANCE hover effect
        if (
          features.length &&
          features[0].layer.source == "designatedroutes-patrol-source"
        ) {
          lastHover = "designatedroutes-patrol-source";
          map.setFilter("designatedroutes-patrol-hover", [
            "all",
            ["in", "name"].concat(features[0].properties.name)
          ]);
        } else if (
          !features.length &&
          lastHover === "designatedroutes-patrol-source"
        ) {
          map.setFilter("designatedroutes-patrol-hover", [
            "all",
            ["in", "name", "abc123"]
          ]);
          lastHover = null;
        }

        // BIKE PARK TRAILS hover effect
        if (
          features.length &&
          features[0].layer.source == "bikepark-trails-source"
        ) {
          lastHover = "designatedroutes-patrol-source";
          map.setFilter("bikepark-trails-hover", [
            "all",
            ["in", "trail_name"].concat(features[0].properties.trail_name)
          ]);
        } else if (
          !features.length &&
          lastHover === "designatedroutes-patrol-source"
        ) {
          map.setFilter("bikepark-trails-hover", [
            "all",
            ["in", "trail_name", "abc123"]
          ]);
          lastHover = null;
        }

        // HIKING TRAILS hover effect
        if (
          features.length &&
          features[0].layer.source == "hiking-trails-source"
        ) {
          lastHover = "hiking-trails-source";
          map.setFilter("hiking-trails-hover", [
            "all",
            ["in", "name"].concat(features[0].properties.name)
          ]);
        } else if (!features.length && lastHover === "hiking-trails-source") {
          map.setFilter("hiking-trails-hover", [
            "all",
            ["in", "name", "abc123"]
          ]);
          lastHover = null;
        }

        // SKI RUNS hover effect
        if (
          features.length &&
          features[0].layer.source == "wintertrails-skiruns-source"
        ) {
          lastHover = "wintertrails-skiruns-source";
          map.setFilter("wintertrails-skiruns-hover", [
            "all",
            ["in", "run_name"].concat(features[0].properties.run_name)
          ]);
        } else if (
          !features.length &&
          lastHover === "wintertrails-skiruns-source"
        ) {
          map.setFilter("wintertrails-skiruns-hover", [
            "all",
            ["in", "run_name", "abc123"]
          ]);
          lastHover = null;
        }

        // HV25KV TRAILS hover effect
        if (
          features.length &&
          features[0].layer.source == "electrical-hv25kv-source"
        ) {
          lastHover = "electrical-hv25kv-source";
          map.setFilter("electrical-hv25kv-hover", [
            "all",
            ["in", "cbl_extent"].concat(features[0].properties.cbl_extent)
          ]);
        } else if (
          !features.length &&
          lastHover === "electrical-hv25kv-source"
        ) {
          map.setFilter("electrical-hv25kv-hover", [
            "all",
            ["in", "cbl_extent", "abc123"]
          ]);
          lastHover === null;
        }

        // Electrical hover effect
        if (
          features.length &&
          features[0].layer.source == "electrical-secondary-source"
        ) {
          lastHover = "electrical-secondary-source";
          map.setFilter("electrical-secondary-hover", [
            "all",
            ["in", "id"].concat(features[0].properties.id)
          ]);
        } else if (
          !features.length &&
          lastHover === "electrical-secondary-source"
        ) {
          map.setFilter("electrical-secondary-hover", [
            "all",
            ["in", "id", "abc123"]
          ]);
          lastHover = null;
        }

        // SEWER GAS LINES hover effect
        if (
          features.length &&
          features[0].layer.source == "utilities-sewergaslines-source"
        ) {
          lastHover = "utilities-sewergaslines-source";
          map.setFilter("utilities-sewergaslines-hover", [
            "all",
            ["in", "extent"].concat(features[0].properties.extent)
          ]);
        } else if (
          !features.length &&
          lastHover === "utilities-sewergaslines-source"
        ) {
          map.setFilter("utilities-sewergaslines-hover", [
            "all",
            ["in", "extent", "abc123"]
          ]);
          lastHover = null;
        }

        // COMM LINES FIBER hover effect
        if (
          features.length &&
          features[0].layer.source == "utilities-commlinesfiber-source"
        ) {
          lastHover = "utilities-commlinesfiber-source";
          map.setFilter("utilities-commlinesfiber-hover", [
            "all",
            ["in", "extent"].concat(features[0].properties.extent)
          ]);
        } else if (
          !features.length &&
          lastHover === "utilities-commlinesfiber-source"
        ) {
          map.setFilter("utilities-commlinesfiber-hover", [
            "all",
            ["in", "extent", "abc123"]
          ]);
          lastHover = null;
        }

        // COMM LINES COPPER hover effect
        if (
          features.length &&
          features[0].layer.source == "utilities-commlinescopper-source"
        ) {
          lastHover = "utilities-commlinescopper-source";
          map.setFilter("utilities-commlinescopper-hover", [
            "all",
            ["in", "extent"].concat(features[0].properties.extent)
          ]);
        } else if (
          !features.length &&
          lastHover === "utilities-commlinescopper-source"
        ) {
          map.setFilter("utilities-commlinescopper-hover", [
            "all",
            ["in", "extent", "abc123"]
          ]);
          lastHover = null;
        }

        // SNOWMAKING PIPELINES hover effect
        if (
          features.length &&
          features[0].layer.source == "snowmaking-pipelines-source"
        ) {
          lastHover = "snowmaking-pipelines-source";
          map.setFilter("snowmaking-pipelines-hover", [
            "all",
            ["in", "pipe_name"].concat(features[0].properties.pipe_name)
          ]);
        } else if (
          !features.length &&
          lastHover === "snowmaking-pipelines-source"
        ) {
          map.setFilter("snowmaking-pipelines-hover", [
            "all",
            ["in", "pipe_name", "abc123"]
          ]);
          lastHover = null;
        }

        // SNOWMAKING POTABLE hover effect
        if (
          features.length &&
          features[0].layer.source == "snowmaking-potable-source"
        ) {
          lastHover = "snowmaking-potable-source";
          map.setFilter("snowmaking-potable-hover", [
            "all",
            ["in", "pipe_name"].concat(features[0].properties.pipe_name)
          ]);
        } else if (
          !features.length &&
          lastHover === "snowmaking-potable-source"
        ) {
          map.setFilter("snowmaking-potable-hover", [
            "all",
            ["in", "pipe_name", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS BLK GONDOLA hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "liftopsdesignatedroutes-blackcombgondola-source"
        ) {
          lastHover = "liftopsdesignatedroutes-blackcombgondola-source";
          map.setFilter("liftopsdesignatedroutes-blackcombgondola-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-blackcombgondola-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-blackcombgondola-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS EXCALIBUR hover effect
        if (
          features.length &&
          features[0].layer.source == "liftopsdesignatedroutes-excalibur-source"
        ) {
          lastHover = "liftopsdesignatedroutes-excalibur-source";
          map.setFilter("liftopsdesignatedroutes-excalibur-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-excalibur-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-excalibur-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS EXCELERATOR hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "liftopsdesignatedroutes-excelerator-source"
        ) {
          lastHover = "liftopsdesignatedroutes-excelerator-source";
          map.setFilter("liftopsdesignatedroutes-excelerator-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-excelerator-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-excelerator-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS SHOWCASE hover effect
        if (
          features.length &&
          features[0].layer.source == "liftopsdesignatedroutes-showcase-source"
        ) {
          lastHover = "liftopsdesignatedroutes-showcase-source";
          map.setFilter("liftopsdesignatedroutes-showcase-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-showcase-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-showcase-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS GLACIEREXPRESS hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "liftopsdesignatedroutes-glacierexpress-source"
        ) {
          lastHover = "liftopsdesignatedroutes-glacierexpress-source";
          map.setFilter("liftopsdesignatedroutes-glacierexpress-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-glacierexpress-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-glacierexpress-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS JERSEYCREAM hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "liftopsdesignatedroutes-jerseycream-source"
        ) {
          lastHover = "liftopsdesignatedroutes-jerseycream-source";
          map.setFilter("liftopsdesignatedroutes-jerseycream-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-jerseycream-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-jerseycream-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS CRYSTAL hover effect
        if (
          features.length &&
          features[0].layer.source == "liftopsdesignatedroutes-crystal-source"
        ) {
          lastHover = "liftopsdesignatedroutes-crystal-source";
          map.setFilter("liftopsdesignatedroutes-crystal-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-crystal-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-crystal-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS SEVENTHHEAVEN hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "liftopsdesignatedroutes-seventhheaven-source"
        ) {
          lastHover = "liftopsdesignatedroutes-seventhheaven-source";
          map.setFilter("liftopsdesignatedroutes-seventhheaven-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-seventhheaven-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-seventhheaven-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS Catskinner hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "liftopsdesignatedroutes-catskinner-source"
        ) {
          lastHover = "liftopsdesignatedroutes-catskinner-source";
          map.setFilter("liftopsdesignatedroutes-catskinner-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-catskinner-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-catskinner-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS magicchair hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "liftopsdesignatedroutes-magicchair-source"
        ) {
          lastHover = "liftopsdesignatedroutes-magicchair-source";
          map.setFilter("liftopsdesignatedroutes-magicchair-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-magicchair-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-magicchair-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS whistlercarpet hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "liftopsdesignatedroutes-whistlercarpet-source"
        ) {
          lastHover = "liftopsdesignatedroutes-whistlercarpet-source";
          map.setFilter("liftopsdesignatedroutes-whistlercarpet-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-whistlercarpet-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-whistlercarpet-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS symphony hover effect
        if (
          features.length &&
          features[0].layer.source == "liftopsdesignatedroutes-symphony-source"
        ) {
          lastHover = "liftopsdesignatedroutes-symphony-source";
          map.setFilter("liftopsdesignatedroutes-symphony-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-symphony-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-symphony-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS franz hover effect
        if (
          features.length &&
          features[0].layer.source == "liftopsdesignatedroutes-franz-source"
        ) {
          lastHover = "liftopsdesignatedroutes-franz-source";
          map.setFilter("liftopsdesignatedroutes-franz-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-franz-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-franz-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS harmony hover effect
        if (
          features.length &&
          features[0].layer.source == "liftopsdesignatedroutes-harmony-source"
        ) {
          lastHover = "liftopsdesignatedroutes-harmony-source";
          map.setFilter("liftopsdesignatedroutes-harmony-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-harmony-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-harmony-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS peakchair hover effect
        if (
          features.length &&
          features[0].layer.source == "liftopsdesignatedroutes-peakchair-source"
        ) {
          lastHover = "liftopsdesignatedroutes-peakchair-source";
          map.setFilter("liftopsdesignatedroutes-peakchair-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-peakchair-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-peakchair-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS emerald hover effect
        if (
          features.length &&
          features[0].layer.source == "liftopsdesignatedroutes emerald-source"
        ) {
          lastHover = "liftopsdesignatedroutes emerald-source";
          map.setFilter("liftopsdesignatedroutes emerald-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes emerald-source"
        ) {
          map.setFilter("liftopsdesignatedroutes emerald-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS olympic hover effect
        if (
          features.length &&
          features[0].layer.source == "liftopsdesignatedroutes-olympic-source"
        ) {
          lastHover = "liftopsdesignatedroutes-olympic-source";
          map.setFilter("liftopsdesignatedroutes-olympic-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-olympic-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-olympic-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS whistlertbar hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "liftopsdesignatedroutes-whistlertbar-source"
        ) {
          lastHover = "liftopsdesignatedroutes-whistlertbar-source";
          map.setFilter("liftopsdesignatedroutes-whistlertbar-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-whistlertbar-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-whistlertbar-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS redchair hover effect
        if (
          features.length &&
          features[0].layer.source == "liftopsdesignatedroutes-redchair-source"
        ) {
          lastHover = "liftopsdesignatedroutes-redchair-source";
          map.setFilter("liftopsdesignatedroutes-redchair-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-redchair-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-redchair-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS garbanzo hover effect
        if (
          features.length &&
          features[0].layer.source == "liftopsdesignatedroutes-garbanzo-source"
        ) {
          lastHover = "liftopsdesignatedroutes-garbanzo-source";
          map.setFilter("liftopsdesignatedroutes-garbanzo-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-garbanzo-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-garbanzo-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS creeksidegondola hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "liftopsdesignatedroutes-creeksidegondola-source"
        ) {
          lastHover = "liftopsdesignatedroutes-creeksidegondola-source";
          map.setFilter("liftopsdesignatedroutes-creeksidegondola-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-creeksidegondola-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-creeksidegondola-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS blackcombcarpet hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "liftopsdesignatedroutes-blackcombcarpet-source"
        ) {
          lastHover = "liftopsdesignatedroutes-blackcombcarpet-source";
          map.setFilter("liftopsdesignatedroutes-blackcombcarpet-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-blackcombcarpet-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-blackcombcarpet-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // LIFT OPS fitzsimmons hover effect
        if (
          features.length &&
          features[0].layer.source ==
            "liftopsdesignatedroutes-fitzsimmons-source"
        ) {
          lastHover = "liftopsdesignatedroutes-fitzsimmons-source";
          map.setFilter("liftopsdesignatedroutes-fitzsimmons-hover", [
            "all",
            ["in", "label"].concat(features[0].properties.label)
          ]);
        } else if (
          !features.length &&
          lastHover === "liftopsdesignatedroutes-fitzsimmons-source"
        ) {
          map.setFilter("liftopsdesignatedroutes-fitzsimmons-hover", [
            "all",
            ["in", "label", "abc123"]
          ]);
          lastHover = null;
        }

        // Change the cursor style on hover over any feature
        // except landuse, clusters, cluster-count..
        map.getCanvas().style.cursor =
          features.length &&
          features[0].layer.source != "wintertrails-callouts-source" &&
          features[0].layer.source != "landuse-cratenure-source" &&
          features[0].layer.source != "drawPoint" &&
          features[0].layer.id != "clusters" &&
          features[0].layer.id != "cluster-count" &&
          features[0].layer.id != "patrol-permanentposts-labels" &&
          features[0].layer.id != "designatedroutes-parking"
            ? "pointer"
            : "";
        if (!features.length) {
          return;
        }
      });
    },
    mapMoveEnd() {
      // Commit a mutation with center/bearing/pitch/zoom level after every map move.
      this.$store.commit("mutationSetViewport", {
        center: this.map.getCenter(),
        bearing: this.map.getBearing(),
        pitch: this.map.getPitch(),
        zoom: this.map.getZoom()
      });
    },
    // When map is clicked
    mapClicked(map, e) {
      const features = map.queryRenderedFeatures(e.point);

      const feature = features[0];

      if (!features.length) {
        return;
      }
      switch (feature.layer.source) {
        case "fanguns":
          this.methodToggleSingleLayer(
            "snowmaking",
            "fanguns",
            true,
            feature.properties.id
          );
          break;
        case "airwaterguns":
          this.methodToggleSingleLayer(
            "snowmaking",
            "airwaterguns",
            true,
            feature.properties.id
          );
          break;
        case "hydrants":
          // don't allow clicking on clusters or cluster-count labels
          if (
            feature.layer.id != "clusters" &&
            feature.layer.id != "cluster-count"
          ) {
            this.methodToggleSingleLayer(
              "snowmaking",
              "hydrants",
              true,
              feature.properties.id
            );
            // Remove popup after it's been created (150ms)
            setTimeout(() => {
              popup.remove();
            }, 150);
            break;
          } else {
            break;
          }
        case "hydrantslabelless":
          this.methodToggleSingleLayer(
            "snowmaking",
            "hydrants",
            true,
            feature.properties.id
          );
          break;
        case "snowmaking-pipeleaks-source":
          this.methodToggleSingleLayer(
            "snowmaking",
            "pipeleaks",
            true,
            feature.properties.id
          );
          break;
        case "snowmaking-potable-source":
          this.methodToggleSingleLayer(
            "snowmaking",
            "potable",
            true,
            feature.properties.id
          );
          break;
        case "snowmaking-images-source":
          this.methodToggleSingleLayer(
            "snowmaking",
            "images",
            true,
            feature.properties.id
          );
          break;
        case "snowmaking-imagesunderground-source":
          this.methodToggleSingleLayer(
            "snowmaking",
            "images",
            true,
            feature.properties.id
          );
          break;
        case "snowmaking-imagesmaintenance-source":
          this.methodToggleSingleLayer(
            "snowmaking",
            "images",
            true,
            feature.properties.id
          );
          break;
        case "snowmaking-imagesoperations-source":
          this.methodToggleSingleLayer(
            "snowmaking",
            "images",
            true,
            feature.properties.id
          );
          break;
        case "snowmaking-imagesother-source":
          this.methodToggleSingleLayer(
            "snowmaking",
            "images",
            true,
            feature.properties.id
          );
          break;
        case "circuits":
          this.methodToggleSingleLayer(
            "electrical",
            "circuits",
            true,
            feature.properties.id
          );
          break;
        case "liftmaintenance-cables-source":
          this.methodToggleSingleLayer(
            "liftmaintenance",
            "liftcables",
            true,
            feature.properties.id
          );
          break;
        case "liftmaintenance-towers-source":
          this.methodToggleSingleLayer(
            "liftmaintenance",
            "lifttowers",
            true,
            feature.properties.id
          );
          break;
        case "utilities-sewergaslines-source":
          this.methodToggleSingleLayer(
            "utilities",
            "sewergaslines",
            true,
            feature.properties.id
          );
          break;
        case "utilities-sewergaspoints-source":
          this.methodToggleSingleLayer(
            "utilities",
            "sewergaspoints",
            true,
            feature.properties.id
          );
          break;
        case "utilities-commlinescopper-source":
          this.methodToggleSingleLayer(
            "utilities",
            "commlinescopper",
            true,
            feature.properties.id
          );
          break;
        case "utilities-commlinesfiber-source":
          this.methodToggleSingleLayer(
            "utilities",
            "commlinesfiber",
            true,
            feature.properties.id
          );
          break;
        case "bikepark-trails-source":
          this.methodToggleSingleLayer(
            "bikepark",
            "trails",
            true,
            feature.properties.id
          );
          break;
        case "bikepark-ehspoints-source":
          this.methodToggleSingleLayer(
            "bikepark",
            "ehspoints",
            true,
            feature.properties.id
          );
          break;
        case "bikepark-signs-source":
          this.methodToggleSingleLayer(
            "bikepark",
            "signs",
            true,
            feature.properties.id
          );
          break;
        case "bikepark-signs2-source":
          this.methodToggleSingleLayer(
            "bikepark",
            "signs",
            true,
            feature.properties.id
          );
          break;
        case "bikepark-signs3-source":
          this.methodToggleSingleLayer(
            "bikepark",
            "signs",
            true,
            feature.properties.id
          );
          break;
        case "bikepark-signs4-source":
          this.methodToggleSingleLayer(
            "bikepark",
            "signs",
            true,
            feature.properties.id
          );
          break;
        case "bikepark-signs5-source":
          this.methodToggleSingleLayer(
            "bikepark",
            "signs",
            true,
            feature.properties.id
          );
          break;
        case "bikepark-bridges-source":
          this.methodToggleSingleLayer(
            "bikepark",
            "bridges",
            true,
            feature.properties.id
          );
          break;
        case "hiking-trails-source":
          this.methodToggleSingleLayer(
            "hiking",
            "trails",
            true,
            feature.properties.id
          );
          break;
        case "electrical-hv25kv-source":
          this.methodToggleSingleLayer(
            "electrical",
            "hv25kv",
            true,
            feature.properties.id
          );
          setTimeout(() => {
            popup25kv.remove();
          }, 150);
          break;
        case "electrical-splices-source":
          this.methodToggleSingleLayer(
            "electrical",
            "splices",
            true,
            feature.properties.id
          );
          break;
        case "electrical-substations-source":
          this.methodToggleSingleLayer(
            "electrical",
            "substations",
            true,
            feature.properties.id
          );
          break;
        case "electrical-secondary-source":
          this.methodToggleSingleLayer(
            "electrical",
            "secondary",
            true,
            feature.properties.id
          );
          break;
        case "patrol-permanentposts-source":
          if (feature.layer.id != "patrol-permanentposts-labels") {
            // no click on labels
            this.methodToggleSingleLayer(
              "patrol",
              "permanentposts",
              true,
              feature.properties.id
            );
          }
          break;
        case "patrol-firstaid-source":
          this.methodToggleSingleLayer(
            "patrol",
            "firstaid",
            true,
            feature.properties.id
          );
          break;
        case "patrol-helivac-source":
          this.methodToggleSingleLayer(
            "patrol",
            "helivac",
            true,
            feature.properties.id
          );
          break;
        case "patrol-landmarks-source":
          this.methodToggleSingleLayer(
            "patrol",
            "landmarks",
            true,
            feature.properties.id
          );
          break;
        case "signshop-signage-source":
        case "signshop-infoboard-source":
        case "signshop-banners-source":
        case "signshop-trailsigns-source":
        case "signshop-summersigns-source":
        case "signshop-other-source":
        case "signshop-legal-source":
        case "signshop-maintenanceandoperations-source":
        case "signshop-productsalesandservice-source":
        case "signshop-foodandbeverage-source":
        case "signshop-marketingandsales-source":
        case "signshop-snowschool-source":
        case "signshop-baseareaoperations-source":
          this.methodToggleSingleLayer(
            "signshop",
            "signage",
            true,
            feature.properties.id
          );
          break;
        case "designatedroutes-standard-source":
          this.methodToggleSingleLayer(
            "designatedroutes",
            "standard",
            true,
            feature.properties.id
          );
          break;
        case "designatedroutes-patrol-source":
          this.methodToggleSingleLayer(
            "designatedroutes",
            "patrol",
            true,
            feature.properties.id
          );
          break;
        case "designatedroutes-snowmaking-source":
          this.methodToggleSingleLayer(
            "designatedroutes",
            "snowmaking",
            true,
            feature.properties.id
          );
          break;
        case "designatedroutes-maintenance-source":
          this.methodToggleSingleLayer(
            "designatedroutes",
            "maintenance",
            true,
            feature.properties.id
          );
          break;
        case "designatedroutes-rubbertirebikepark-source":
          this.methodToggleSingleLayer(
            "designatedroutes",
            "rubbertirebikepark",
            true,
            feature.properties.id
          );
          break;
        case "designatedroutes-rubbertireelectricalmaintenance-source":
          this.methodToggleSingleLayer(
            "designatedroutes",
            "rubbertireelectricalmaintenance",
            true,
            feature.properties.id
          );
          break;
        case "designatedroutes-rubbertireliftmaintenance-source":
          this.methodToggleSingleLayer(
            "designatedroutes",
            "rubbertireliftmaintenance",
            true,
            feature.properties.id
          );
          break;
        case "designatedroutes-rubbertirepatrol-source":
          this.methodToggleSingleLayer(
            "designatedroutes",
            "rubbertirepatrol",
            true,
            feature.properties.id
          );
          break;
        case "designatedroutes-rubbertiresnowmaking-source":
          this.methodToggleSingleLayer(
            "designatedroutes",
            "rubbertiresnowmaking",
            true,
            feature.properties.id
          );
          break;
        case "designatedroutes-rubbertirestandard-source":
          this.methodToggleSingleLayer(
            "designatedroutes",
            "rubbertirestandard",
            true,
            feature.properties.id
          );
          break;
        case "grooming-winch-source":
          this.methodToggleSingleLayer(
            "grooming",
            "winch",
            true,
            feature.properties.id
          );
          break;
        case "wintertrails-skiruns-source":
          this.methodToggleSingleLayer(
            "wintertrails",
            "skiruns",
            true,
            feature.properties.id
          );
          break;
        case "wintertrails-landmarks-source":
          this.methodToggleSingleLayer(
            "wintertrails",
            "landmarks",
            true,
            feature.properties.id
          );
          break;
        case "wintertrails-skirunpolygons-source":
          this.methodToggleSingleLayer(
            "wintertrails",
            "skirunpolygons",
            true,
            feature.properties.id
          );
          break;
        case "snowmaking-pipelines-source":
          this.methodToggleSingleLayer(
            "snowmaking",
            "pipelines",
            true,
            feature.properties.id
          );
          break;
        case "buildingmaintenance-buildings-source":
          this.methodToggleSingleLayer(
            "buildingmaintenance",
            "buildings",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-blackcombgondola-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "blackcombgondola",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-excelerator-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "excelerator",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-excalibur-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "excalibur",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-showcase-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "showcase",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-glacierexpress-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "glacierexpress",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-jerseycream-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "jerseycream",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-crystal-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "crystal",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-seventhheaven-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "seventhheaven",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-catskinner-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "catskinner",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-magicchair-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "magicchair",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-whistlercarpet-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "whistlercarpet",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-symphony-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "symphony",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-franz-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "franz",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-harmony-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "harmony",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-peakchair-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "peakchair",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-emerald-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "emerald",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-olympic-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "olympic",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-whistlertbar-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "whistlertbar",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-redchair-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "redchair",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-garbanzo-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "garbanzo",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-creeksidegondola-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "creeksidegondola",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-blackcombcarpet-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "blackcombcarpet",
            true,
            feature.properties.id
          );
          break;
        case "liftopsdesignatedroutes-fitzsimmons-source":
          this.methodToggleSingleLayer(
            "liftopsdesignatedroutes",
            "fitzsimmons",
            true,
            feature.properties.id
          );
          break;
        case "summerops-fireboxes-source":
          this.methodToggleSingleLayer(
            "summerops",
            "fireboxes",
            true,
            feature.properties.id
          );
          break;
      }
    }
  }
};
</script>
